import React, { useCallback, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import get from 'lodash/get';
import { Card } from '@els/els-react--card';
import SubGroupMultipleDatabases from '@Containers/ResultsPreview/GroupMultipleDatabases/SubGroupMultipleDatabases';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import BlockLabel from '@Containers/ResultsPreview/Components/BlockLabel';
import BlockFeedbacks from '@Containers/ResultsPreview/Components/BlockFeedbacks';
import BlockActionButtons from '@Containers/ResultsPreview/Components/BlockActionButtons';
import ResultsPreviewAIBanner from '@Containers/ResultsPreview/Components/ResultsPreviewAIBanner';

import './GroupMultipleDatabases.scss';

function GroupMultipleDatabases({
  previewItem,
  showMoreResults,
  showStructurePopup,
  copyQuery,
  createAlert,
  redirectToResults,
  cancelQuery,
  cancelQueriesInGroup,
  expandedByDefault,
}) {
  const QUERY_CANCELLED_PATH = 'query.cancelled';
  const showProgressLoader = !previewItem.fullyLoaded && !get(previewItem, QUERY_CANCELLED_PATH);
  const showQueryCancelled = !previewItem.fullyLoaded && get(previewItem, QUERY_CANCELLED_PATH);
  const showMultipleDatabasesExpand = previewItem.groupHasResults;
  const showPredictionButton = previewItem.fullyLoaded && !get(previewItem, QUERY_CANCELLED_PATH);
  const iconName = get(previewItem, 'title', '')?.toLowerCase().replace(' ', '-');

  const [expanded, setExpanded] = useState(false);
  const previewListRef = useRef(null);
  const viewResultsButtonRef = useRef(null);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  useEffect(() => {
    if (expandedByDefault && previewItem.groupHasResults) {
      setExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewItem.groupHasResults]);

  useDidUpdateEffect(() => {
    if (expanded) {
      previewListRef.current?.focus();
    } else {
      viewResultsButtonRef.current?.focus();
    }
  }, [expanded]);

  return (
    <div
      className={cn('quick-results-preview__item e2e-quick-results-item group-multiple-databases', {
        'quick-results-preview__item-loading': !previewItem.fullyLoaded,
        'u-els-margin-bottom-2x': previewItem.semanticSearchIncluded,
      })}>
      <div className={`quick-results-preview__item_image quick-results-preview__item_image-${iconName}`} />
      <Card className="quick-results-preview__item_wrapper">
        {previewItem.semanticSearchIncluded && <ResultsPreviewAIBanner />}
        <div className="quick-results-preview__item_controls">
          <BlockLabel previewItem={previewItem} showResultsCount={previewItem.fullyLoaded && !previewItem.groupHasResults} showTitle />
          <div className="quick-results-preview__item_controls_wrapper">
            <BlockFeedbacks
              previewItem={previewItem}
              showStructurePopup={showStructurePopup}
              copyQuery={copyQuery}
              createAlert={createAlert}
              showFeedbacks
            />
          </div>
          <BlockActionButtons
            previewItem={previewItem}
            showProgressLoader={showProgressLoader}
            onCancelClick={() => cancelQueriesInGroup(previewItem.groupId)}
            showQueryCancelled={showQueryCancelled}
            showExpandButton={showMultipleDatabasesExpand}
            viewResultsButtonRef={viewResultsButtonRef}
            expandButtonConfig={{
              label: 'Multiple databases',
              e2eName: 'multiple-databases',
            }}
            expandButtonAttrs={{
              'aria-label': 'Multiple results from different databases',
            }}
            expanded={expanded}
            toggleExpanded={toggleExpanded}
            showViewResultsButton={false}
            showPredictionButton={showPredictionButton}
            showBioactivityVisualizationButton={false}
          />
        </div>

        {expanded && (
          <ul
            ref={previewListRef}
            tabIndex={-1}
            className={`quick-results-preview__item_preview u-els-margin-top quick-results-preview__item_preview-${iconName}`}>
            {previewItem?.multipleDatabasesItems.map((subItem, subItemIndex) => (
              <SubGroupMultipleDatabases
                key={subItem.queryId}
                previewItem={subItem}
                previewItemIndex={subItemIndex}
                showMoreResults={showMoreResults}
                redirectToResults={redirectToResults}
                cancelQuery={cancelQuery}
                isLast={subItemIndex === previewItem.multipleDatabasesItems.length - 1}
              />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

GroupMultipleDatabases.propTypes = {
  previewItem: PropTypes.object.isRequired,
  showMoreResults: PropTypes.func.isRequired,
  showStructurePopup: PropTypes.func.isRequired,
  copyQuery: PropTypes.func.isRequired,
  createAlert: PropTypes.func.isRequired,
  redirectToResults: PropTypes.func.isRequired,
  cancelQuery: PropTypes.func.isRequired,
  cancelQueriesInGroup: PropTypes.func.isRequired,
  expandedByDefault: PropTypes.bool,
};

export default GroupMultipleDatabases;
